import { Base } from './base.model';

export interface Questionnaire extends Base {
  id: string;
  created_at: Date;
  name: string;
  version: string;
  slug: string;
  apply_from: Date;
  apply_until: Date;
  cp_questionnaire_page: QuestionnairePage[];
  first_page: string;
  active: boolean;
  audience: PublicAudience | PrivateAudience | CaseAudience;
  configuration: PageConfig;
  case_id: string;
  status: string;

  SkipIf?: {
    aggregator: 'any' | 'none' | 'all';
    conditions: {
      key: string;
      condition:
        | '$eq'
        | '$dif'
        | '$neq'
        | '$includes'
        | '$contains'
        | '$notContains'
        | '$exists';
      value: string | boolean;
    }[];
  };
}

export interface QuestionInPage extends Base {
  id: string;
  page_id: string;
  priority: number;
  question_id: string;
  question_logic?: { validate?: Logic; hide_if?: Logic; required?: boolean };
  cp_question: Question;
  claim_id: string;
}

export interface Question extends Base {
  id: string;
  created_at: Date;
  name: string;
  slug: string;
  statement: string;
  response_type: QuestionType;
  configuration:
    | any
    | QuestionConfigurationMultiFile
    | QuestionConfigurationOptions
    | QuestionConfigurationMultiStepper
    | QuestionConfigurationTable
    | QuestionConfigurationSearchList;
  questionnaire_id: string;
  elective?: boolean;

  // front-end utils
  answer?: Answer;
  isValid?: boolean;
  messageError?: string;
  isBlockedByAnswerBehavior?: boolean;
}

/**
 * Per-field format rule for the WORLD_ADDRESS question type (CPK-4150).
 * `denyPattern` is a regex (as string) matching characters that are NOT allowed.
 */
export interface AddressFieldValidationRule {
  minLength?: number;
  denyPattern?: string;
  requireAlphanumeric?: boolean;
  /** Only meaningful for `postalCode`, applied when the address country is Brazil. */
  brazilExactDigits?: number;
}

export interface AddressFieldValidationConfig {
  streetName?: AddressFieldValidationRule;
  streetNumber?: AddressFieldValidationRule;
  postalCode?: AddressFieldValidationRule;
  /** Applied to every address field in addition to any field-specific rule above. */
  _global?: { denyPattern?: string };
}

export interface WorldAddressQuestion extends Question {
  configuration: {
    /**
     *Used to hide the county input field.
     */
    hideCounty?: boolean;
    /** CPK-4150: regex/format rules per field, configurable without a deploy. */
    fieldValidation?: AddressFieldValidationConfig;
    /** CPK-4150: ISO 3166-1 alpha-2 code (e.g. "BR") to restrict Google Places results. */
    countryRestriction?: string;
  };
}

export interface QuestionTable extends Question {
  configuration: QuestionConfigurationTable;
  answer?: {
    value: string | number | Date | null;
    addtional_information?: {
      [slug: string]: Answer;
    }[];
    isIgnored?: boolean;
  };
}

export interface QuestionMultiStepper extends Question {
  configuration: QuestionConfigurationMultiStepper;
  answer?: {
    value: string | number | Date | null;
    addtional_information?: {
      [key: string]: number;
    };
    isIgnored?: boolean;
  };
}

export interface QuestionWithContext extends Question {
  claim_id: string;
}

export interface Answer {
  value: string | number | Date | null;
  addtional_information?: any;
  isIgnored?: boolean;
}

export interface TableAnswer {
  id: string;
  priority: number;
  questions: QuestionInPage[];
}

export interface QuestionnaireAnswer extends Base {
  id: string;
  created_at: Date;
  answers: { [key: string]: Answer };
  completed_at: Date | null;
  started_at: Date;
  status: 'OPEN' | 'SUBMITTED';
  user_data: any;
  campaign_data: any;
  claim_id: string;
  questionnaire_id: string;
  history: any;
  responsible_user_id: string;
  navstack: string[] | any[];
  simple_questionnaire?: boolean;
}

export interface PageConfig {
  skip_if?: Logic;
  agent_impersonate_skip?: boolean;
  simple_questionnaire?: boolean;
}

export interface QuestionAddressValidation {
  questionSlug: string;
  status: 'passed' | 'failed' | 'skipped';
  possible_next_action?: 'ACCEPT' | 'CONFIRM' | 'CONFIRM_ADD_SUBPREMISES' | 'FIX';
  flagged?: boolean;
  validation_granularity?: string;
  formatted_address?: string;
  response_id?: string;
  checked_at?: string;
  retry_limit_reached?: boolean;
  /** CPK-4239 fix: true when the caller (AGENT/ADMINISTRATOR) has no retry ceiling — lets the
   *  prompt offer the self-certify override even though retry_limit_reached never fires for them. */
  caller_unlimited_retries?: boolean;
  address?: Record<string, any>;
  llm_suspicion?: {
    suspicious: boolean;
    confidence_score?: number;
    reasoning?: string;
    provider_used: 'bedrock' | 'none';
    checked_at: string;
  };
}

export interface Logic {
  aggregator: 'any' | 'none' | 'all';
  conditions: {
    key: string;
    condition:
      | '$eq'
      | '$dif'
      | '$neq'
      | '$includes'
      | '$contains'
      | '$notContains'
      | '$exists'
      | '$dateEq'
      | '$dateBefore'
      | '$dateAfter'
      | '$dateBetween';
    value: string | boolean;
    if_error?: 'hide' | 'show';
  }[];
}

export interface QuestionnairePage {
  id: string;
  code: string;
  name: string;
  cp_question_in_page: QuestionInPage[];
  page_config: PageConfig;
  questionnaire_id: string;
  next_page_id: string | null;
}

export interface Audience {
  audience_type: 'PUBLIC' | 'PRIVATE' | 'CASE';
}

interface PublicAudience extends Audience {
  audience_type: 'PUBLIC';
}

interface PrivateAudience extends Audience {
  audience_type: 'PRIVATE';
  user_tags_included: string[];
  user_tags_excluded: string[];
  claim_tags_included: string[];
  claim_tags_excluded: string[];
}

interface CaseAudience extends Audience {
  audience_type: 'CASE';
}

export interface QuestionnaireWithQuestions {
  questionnaire: Questionnaire;
  questions_map: { [id: string]: QuestionInPage };
}

interface QuestionConfiguration {}

export interface AnswerBehavior {
  agent: boolean;
  answerValue: string;
  block: boolean;
  message: string;
}

export interface QuestionConfigurationOptions extends QuestionConfiguration {
  options: { key: string; value: string }[];
  answer_behavior?: AnswerBehavior[];
}

export interface QuestionConfigurationSearchList extends QuestionConfiguration {
  options: { key: string; value: string }[];
  maxSelections?: number | null;
}

export interface QuestionConfigurationMultiStepper extends QuestionConfiguration {
  options: {
    key: string;
    value: string;
    default_value: number;
    min: number;
    max: number;
  }[];
}

interface QuestionConfigurationMultiFile extends QuestionConfiguration {
  bucket: string;
  maxFileSize?: number;
  limit?: number;
}

export interface QuestionTableConfigQuestions extends Question {
  priority: number;
  logic?: { validate?: Logic; hide_if?: Logic; required?: boolean };
}
export interface QuestionConfigurationTable extends QuestionConfiguration {
  prefixSection?: string;
  sectionName?: string;
  table: QuestionTableConfigQuestions[];
}

export enum QuestionType {
  'SHORT_TEXT' = 'SHORT_TEXT',
  'LONG_TEXT' = 'LONG_TEXT',
  'OPTIONS' = 'OPTIONS',
  'MULTI_OPTIONS' = 'MULTI_OPTIONS',
  'FILE' = 'FILE',
  'MULTI_FILE' = 'MULTI_FILE',
  'CURRENCY' = 'CURRENCY',
  'INTEGER' = 'INTEGER',
  'DECIMAL' = 'DECIMAL',
  'DATE' = 'DATE',
  'DATETIME' = 'DATETIME',
  'PHONE' = 'PHONE',
  'CPF' = 'CPF',
  'CPF_LEAK_CHECK' = 'CPF_LEAK_CHECK',
  'ADDRESS' = 'ADDRESS',
  'YESNO' = 'YESNO',
  'TOGGLE' = 'TOGGLE',
  'EXPLANATION' = 'EXPLANATION',
  'TABLE' = 'TABLE',
  'CONCAT' = 'CONCAT',
  'MULTI_STEPPER' = 'MULTI_STEPPER',
  'SIGNATURE' = 'SIGNATURE',
  'CAMERA_FILE' = 'CAMERA_FILE',
  'MULTI_CAMERA_FILE' = 'MULTI_CAMERA_FILE',
  'SEARCH_LIST' = 'SEARCH_LIST',
  'API' = 'API',
}

export type ApiQuestionKind = 'CPFLEAK' | 'CUSTOM' | 'RGP';
export type ApiQuestionMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiQuestionTriggerRule {
  /** Required minimum length of the stringified value. */
  minLength?: number;
  /** Required maximum length. */
  maxLength?: number;
  /** Required regex match (passed to `new RegExp`). */
  regex?: string;
}

export interface ApiQuestionConfiguration {
  api: {
    type: ApiQuestionKind;
    /** When false (default), the input field is not rendered — only the statement/explanation. */
    visible?: boolean;
    /**
     * Optional gate — request only fires when each declared per-slug rule passes.
     * Use to avoid hitting the API for partial inputs (e.g. CPF with fewer than 11 digits).
     */
    trigger?: {
      rules?: { [slug: string]: ApiQuestionTriggerRule };
    };
    request: {
      method: ApiQuestionMethod;
      /** Ignored for CPFLEAK (uses SIGILO_API_URL on the backend). Required for CUSTOM. */
      url: string;
      /** Each value is either a literal string or an `answers.{slug}.value` reference. */
      body?: { [key: string]: string };
      /** Each value may also include `$ssm:/path` or `$secret:name` markers (CUSTOM only). */
      headers?: { [key: string]: string };
    };
    response: {
      responseMapping: {
        /** JSONPath into the upstream response (e.g. "$.data.score") for the answer value. */
        answer: string;
        /** Optional map of named JSONPath extractions stored in addtional_information. */
        additionalInfo?: { [key: string]: string };
      };
    };
  };
}
